/**
 * File upload handler with IndexedDB persistence
 * @typedef {Object} FileUploadState
 * @property {string} storageKey - Unique identifier for the file in IndexedDB
 * @property {string} fileName - Name of the uploaded file
 * @property {string} fileTempURL - Temp URL of the file to let user download
 * @property {string|null} fileData - Base64 data URL of the file content
 */

/**
 * Creates a new file upload handler instance
 * @param {Object} params - Initialization parameters
 * @param {string} [params.storageKey] - Initial storage key, typically provided by server
 * @param {number} [params.maxFileSize=5] - Maximum file size in MB (defaults to 5MB)
 * @returns {FileUploadState} File upload state and methods
 */
export default ({ storageKey, maxFileSize = 5 }) => ({
  storageKey,
  fileName: "",
  fileTempURL: "",
  fileData: null,
  DB_NAME: "FileUploadDB",
  STORE_NAME: "files",
  maxFileSize,

  /**
   * Opens IndexedDB connection
   * @returns {Promise<IDBDatabase>}
   */
  async openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, {
            keyPath: "key",
          });
          // Add timestamp index for expire policy if needed
          store.createIndex("timestamp", "timestamp", { unique: false });
        }
      };
    });
  },

  /**
   * Cleans up database, removing all files except the current one
   * @param {string} currentKey - Key of the file to keep
   * @returns {Promise<void>}
   */
  async cleanupDB(currentKey) {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.STORE_NAME], "readwrite");
      const store = transaction.objectStore(this.STORE_NAME);

      // Get all keys and delete everything except current file
      const request = store.getAllKeys();

      request.onsuccess = () => {
        const keys = request.result;
        keys.forEach((key) => {
          if (key !== currentKey) {
            store.delete(key);
          }
        });
      };

      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
    });
  },

  /**
   * Saves file data to IndexedDB and cleans up old files
   * @param {string} key - Storage key
   * @param {Object} data - File data to store
   * @returns {Promise<void>}
   */
  async saveToDB(key, data) {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.STORE_NAME], "readwrite");
      const store = transaction.objectStore(this.STORE_NAME);

      // Add timestamp to data
      const fileData = {
        key,
        ...data,
        timestamp: new Date().getTime(),
      };

      const request = store.put(fileData);

      request.onerror = () => reject(request.error);
      request.onsuccess = async () => {
        try {
          // Clean up old files after successful save
          await this.cleanupDB(key);
          resolve();
        } catch (e) {
          console.warn("Error during cleanup:", e);
          resolve(); // Still resolve as the file was saved successfully
        }
      };

      transaction.oncomplete = () => db.close();
    });
  },

  /**
   * Retrieves file data from IndexedDB
   * @param {string} key - Storage key to retrieve
   * @returns {Promise<Object|null>}
   */
  async getFromDB(key) {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.STORE_NAME], "readonly");
      const store = transaction.objectStore(this.STORE_NAME);

      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      transaction.oncomplete = () => db.close();
    });
  },

  /**
   * Removes file data from IndexedDB
   * @param {string} key - Storage key to remove
   * @returns {Promise<void>}
   */
  async removeFromDB(key) {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.STORE_NAME], "readwrite");
      const store = transaction.objectStore(this.STORE_NAME);

      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();

      transaction.oncomplete = () => db.close();
    });
  },

  /**
   * Generates a unique storage key from file data
   * @param {string} fileData - Base64 data URL of file content
   * @returns {Promise<string>} Storage key prefixed with '__hrx_file__'
   */
  async generateStorageKey(fileData) {
    // Convert the complete data URL string to bytes for hashing
    const encoder = new TextEncoder();
    const data = encoder.encode(fileData);

    // Generate SHA-256 hash of the data
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    // Convert hash buffer to hexadecimal string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    return `__hrx_file__${hashHex}`;
  },

  /**
   * Initializes the file input with data from IndexedDB if available
   * @returns {Promise<void>}
   */
  async init() {
    // Only proceed if we have a storage key (typically from server)
    if (this.storageKey) {
      try {
        const savedFile = await this.getFromDB(this.storageKey);
        if (savedFile) {
          this.fileName = savedFile.name;
          this.fileTempURL = URL.createObjectURL(savedFile)
          this.fileData = savedFile.data;

          // Create and assign file to input
          const dataTransfer = new DataTransfer();
          const file = this.base64ToFile(
            savedFile.data,
            savedFile.name,
            savedFile.type,
          );
          dataTransfer.items.add(file);
          this.$refs.input.files = dataTransfer.files;

          // Notify listeners that file is attached
          document.dispatchEvent(new Event("file_attached"));
        }
      } catch (e) {
        console.warn("Error retrieving file data:", e);
        await this.removeFromDB(this.storageKey);
      }
    }
  },

  /**
   * Handles file upload events
   * @returns {Promise<void>}
   */
  async fileHandler() {
    let file = this.$refs.input.files[0];
    if (file) {
      // Check file size limit (configurable, defaults to 5MB)
      if (file.size / 1024 / 1024 > this.maxFileSize) {
        alert(
          `File size exceeds ${this.maxFileSize}MB. Please upload a file up to ${this.maxFileSize}MB.`,
        );
        // Clear all file data
        this.$refs.input.value = "";
        if (this.storageKey) {
          await this.removeFromDB(this.storageKey);
        }
        this.fileName = "";
        this.fileTempURL = null;
        this.fileData = null;
        this.storageKey = null;
      } else {
        this.fileName = file.name;
        this.fileTempURL = URL.createObjectURL(file)

        // Convert file to data URL and save to localStorage
        const reader = new FileReader();
        reader.onload = async (e) => {
          // Store the data URL (format: "data:mime/type;base64,...")
          this.fileData = e.target.result;

          // Generate unique storage key based on file content
          this.storageKey = await this.generateStorageKey(this.fileData);

          try {
            await this.saveToDB(this.storageKey, {
              name: file.name,
              type: file.type,
              data: this.fileData,
            });
            document.dispatchEvent(new Event("file_attached"));
          } catch (e) {
            console.error("Error saving file:", e);
            alert("Error saving file. Please try again.");
          }
        };
        reader.readAsDataURL(file);
      }
    }
  },

  /**
   * Converts base64 data URL back to a File object
   * @param {string} base64Data - Base64 data URL of file content
   * @param {string} filename - Name of the file
   * @param {string} type - MIME type of the file
   * @returns {File} Reconstructed File object
   */
  base64ToFile(base64Data, filename, type) {
    // Extract base64 data from data URL and convert to binary
    const binaryString = window.atob(base64Data.split(",")[1]);
    const bytes = new Uint8Array(binaryString.length);

    // Convert binary string to byte array
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create Blob and File objects from binary data
    const blob = new Blob([bytes], { type: type });
    return new File([blob], filename, { type: type });
  },
});
